import { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BookOpen, FileText, ShieldAlert, ArrowLeft, Printer } from 'lucide-react';
import { toast } from 'sonner';

function Help() {
  const { doc } = useParams(); // 'manual' | 'executive' | 'formal'
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [meta, setMeta] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const isAdmin = user?.role === 'admin';
  const DOC_KEY_MAP = {
    executive: 'executive_doc',
    formal: 'user_manual_formal',
    manual: 'user_manual',
  };
  const docKey = DOC_KEY_MAP[doc] || 'user_manual';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setDenied(false);
      try {
        const metaRes = await fetch('/docs/docs.json', { cache: 'no-store' });
        const metaJson = await metaRes.json();
        if (cancelled) return;
        setMeta(metaJson);
        const docMeta = metaJson.documents[docKey];
        if (!docMeta) { setDenied(true); return; }
        if (docMeta.admin_only && !isAdmin) { setDenied(true); return; }
        const fileRes = await fetch(docMeta.file, { cache: 'no-store' });
        if (!fileRes.ok) throw new Error('No se pudo cargar el documento');
        const text = await fileRes.text();
        if (!cancelled) setContent(text);
      } catch (err) {
        if (!cancelled) toast.error('Error al cargar el documento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [docKey, isAdmin]);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="help-loading">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (denied) {
    return (
      <Card className="max-w-2xl mx-auto border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800" data-testid="help-denied">
        <CardContent className="pt-6 text-center space-y-3">
          <ShieldAlert className="w-12 h-12 mx-auto text-amber-600" />
          <h2 className="text-xl font-bold">Acceso restringido</h2>
          <p className="text-sm text-muted-foreground">
            El Documento Ejecutivo es de uso exclusivo del Super Admin del sistema.
          </p>
          <Button variant="outline" onClick={() => navigate('/help/manual')}>Volver al Manual de Usuario</Button>
        </CardContent>
      </Card>
    );
  }

  const docMeta = meta?.documents?.[docKey];
  const Icon = docKey === 'executive_doc' ? FileText : BookOpen;
  const tone = docKey === 'executive_doc' ? 'text-rose-600' : 'text-blue-600';

  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Icon className={`w-10 h-10 ${tone} shrink-0 mt-1`} />
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="help-title">{docMeta?.title}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="font-mono" data-testid="help-version">
                {meta?.version}
              </Badge>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground" data-testid="help-date">{meta?.date}</span>
              {docMeta?.admin_only && (
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300 ml-2">
                  Confidencial · Admin
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 print:hidden">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-manual-btn">
            <Printer className="w-4 h-4 mr-2" />Imprimir · PDF
          </Button>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />Volver
          </Button>
        </div>
      </div>

      {/* Estilos de impresión: rompe el overflow del app-shell y deja fluir el manual completo */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm 12mm; }
          html, body {
            background: #fff !important;
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Rompe el overflow/altura de TODOS los ancestros para que el contenido fluya */
          body, body * {
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          /* Oculta visualmente todo el app-shell */
          body * { visibility: hidden; }
          [data-testid="help-page"], [data-testid="help-page"] * { visibility: visible; }
          [data-testid="help-page"] {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            right: 0 !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          [data-testid="help-page"] .print\\:hidden,
          [data-testid="print-manual-btn"] { display: none !important; }
          .prose {
            max-width: none !important;
            font-size: 11pt !important;
            color: #000 !important;
          }
          .prose h1 { font-size: 22pt !important; }
          .prose h2 { font-size: 16pt !important; page-break-after: avoid; page-break-inside: avoid; }
          .prose h3 { font-size: 13pt !important; page-break-after: avoid; }
          .prose table, .prose pre, .prose img { page-break-inside: avoid; }
          .prose a { color: inherit; text-decoration: none; }
        }
      `}</style>

      {/* Markdown content */}
      <Card>
        <CardContent className="pt-6">
          <article
            className="prose prose-slate dark:prose-invert max-w-none
                       prose-headings:font-bold prose-headings:tracking-tight
                       prose-h1:text-3xl prose-h1:border-b prose-h1:pb-3 prose-h1:mb-6
                       prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:pb-2
                       prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                       prose-p:leading-relaxed prose-p:my-3
                       prose-li:my-1
                       prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:hidden prose-code:after:hidden
                       prose-pre:bg-slate-900 prose-pre:text-slate-100
                       prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 dark:prose-blockquote:bg-blue-950/20 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic
                       prose-table:text-sm prose-th:bg-muted prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-tr:border-b
                       prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>{content}</ReactMarkdown>
          </article>
        </CardContent>
      </Card>
    </div>
  );
}

export default Help;
